/* eslint-disable no-underscore-dangle */
import { get, isObject, isDate, isNil, isArray, size } from 'lodash';
import axios from 'axios';
import moment from 'moment-timezone';
import { isEmbedded } from './webshell-embed/embedded';

let currentRefreshTokenRequest = null;

// Only read once at bootstrap, before context.currentTenant has been established.
export const getInitialTenantFromQuery = () => {
    if (typeof window === 'undefined') return null;

    return new URL(window.location.href).searchParams.get('tenant');
};

const encodeAxiosParam = value => {
    let v = value;
    if (isDate(value)) {
        v = value.toISOString();
    } else if (isObject(value)) {
        v = JSON.stringify(value);
    }
    return encodeURIComponent(v);
};

const interceptors = {
    csrfToken: (context, request) => {
        request.headers['x-csrf-token'] = context.csrfToken;
        return request;
    },
    timezone: (store, request) => {
        const logging = get(store.state, 'context.generalConfig.dateFormats.logging', null);
        // collect minutes of difference between current time and UTC
        // different is UTC - now, so we flip to sum based on UTC later
        request.headers['x-timezone-offset'] = 0 - new Date().getTimezoneOffset();
        request.headers['x-usertime'] = moment().format(logging);
        return request;
    },
    currentTenant: (context, request) => {
        const currentTenant = get(context, 'currentTenant');
        const availableTenants = get(context, 'availableTenants', []);

        // availableTenants is only populated once /api/user-context returns, so it can only be
        // used to reject a tenant after it has loaded. The server validates the tenant regardless.
        const isKnownTenant =
            !availableTenants.length ||
            availableTenants.some(availableTenant => availableTenant.key === currentTenant);

        if (currentTenant && isKnownTenant) {
            request.headers['x-rtls-tenant'] = currentTenant;
        }

        return request;
    },
    responseError: (store, router, error) => {
        // Was this cancelled by our code?
        if (axios.isCancel(error)) {
            // If so, pass through the error
            return Promise.reject(error);
        }

        // Unauthenticated?
        if (error.response?.status === 401) {
            // ensure it is not because of a hardcoded login attempt with invalid credentials
            if (error.config.url.endsWith('/login')) return Promise.reject(error);

            // Try first refreshing the token
            const shouldAttemptRefresh =
                error.response.data && error.response.data.attemptTokenRefresh;
            if (
                shouldAttemptRefresh &&
                !error.config.url.endsWith('/api/token/refresh') &&
                !error.config._triedRefreshToken &&
                !error.config._refreshFailed
            ) {
                // Do not attempt multiple requests to refresh in parallel, or they would fail since the last ones will attempt to refresh outdated tokens
                error.config._triedRefreshToken = true;
                if (!currentRefreshTokenRequest) {
                    currentRefreshTokenRequest = store
                        .dispatch('context/refreshUserContext')
                        .then(() => {
                            currentRefreshTokenRequest = null;
                        })
                        .catch(() => {
                            currentRefreshTokenRequest = null;
                            // if the token refresh fails, let the error handler decide what to do with the original request, but mark the request as having failed to refresh
                            error.config._refreshFailed = true;
                            return interceptors.responseError(store, router, error);
                        });
                }

                // Once the token has been successfully refreshed, retry the original request, otherwise just pass back the result of the error handler
                // Any errors with this request would go through the interceptor for the new request, as part of the axios.request() call
                return currentRefreshTokenRequest.then(res => {
                    return !error.config._refreshFailed
                        ? axios.request(error.config)
                        : Promise.resolve(res);
                });
            }

            // Embedded, the shell owns the session and has already acted on a failed refresh,
            // by trying refresh with retries, or sending the user to login.
            // If we end up here, there is something wrong with the shell backend so just fail.
            if (isEmbedded()) return Promise.reject(error);

            // Were we loading the /user-context as part of application startup?
            // If we couldnt load it even after trying refreshing the auth tokens, return empty context and let the bootstrap process to finish
            // The router navigation guard will navigate to the login page (since there will be no profile in the context)
            if (error.config.url.endsWith('/user-context')) {
                return Promise.resolve({ data: null });
            }

            // User needs to authenticate, navigate to the login page (unless already there)
            let preferredLoginPath = localStorage.getItem('preferredLoginPath') || '/login';
            if (router.currentRoute.value.path.endsWith('/login')) {
                preferredLoginPath = router.currentRoute.value.path;
            }

            if (router.currentRoute.value.path !== preferredLoginPath) {
                router.replace({
                    path: preferredLoginPath,
                    query: { returnPath: router.currentRoute.value.path },
                });
            }

            return Promise.reject(error);
        }
        // 403 for token refresh means wrong csrf token in FE request
        // in this case we need to refresh it by loading context
        if (error.response?.status === 403 && error.config.url.endsWith('/api/token/refresh')) {
            store.dispatch('context/loadContext').then(() => router.replace({ path: '/login' }));
        }

        // 403: user is logged in and has no permission to access a resource
        // 477: modsecurity rules fail
        if ([403, 477].includes(error.response?.status)) {
            router.replace({ path: `/error/${error.response.status}` });
        }

        return Promise.reject(error);
    },
    paramsSerializer: params => {
        return Object.entries(params)
            .reduce((currentParams, [key, value]) => {
                if (!isNil(value) && (isArray(value) ? !!size(value) : true)) {
                    if (Array.isArray(value)) {
                        const arrayValues = value.map(v => `${key}[]=${encodeAxiosParam(v)}`);
                        return currentParams.concat(arrayValues);
                    }
                    currentParams.push(`${key}=${encodeAxiosParam(value)}`);
                }
                return currentParams;
            }, [])
            .join('&');
    },
};

// Ids of the interceptors the last install added. Embedded, promo is mounted again on every visit
// and axios is a module singleton, so without ejecting them each remount stacks another set and
// keeps the earlier routers alive.
let installedInterceptors = null;

const uninstall = () => {
    if (!installedInterceptors) return;
    installedInterceptors.request.forEach(id => axios.interceptors.request.eject(id));
    installedInterceptors.response.forEach(id => axios.interceptors.response.eject(id));
    installedInterceptors = null;
};

const install = (app, options) => {
    uninstall();
    installedInterceptors = {
        request: [
            axios.interceptors.request.use(
                interceptors.csrfToken.bind(null, options.store.state.context)
            ),
            axios.interceptors.request.use(interceptors.timezone.bind(null, options.store)),
            axios.interceptors.request.use(
                interceptors.currentTenant.bind(null, options.store.state.context)
            ),
            axios.interceptors.request.use(config => {
                config.paramsSerializer = {
                    serialize: params => interceptors.paramsSerializer(params),
                };
                return config;
            }),
        ],
        response: [
            axios.interceptors.response.use(
                null,
                interceptors.responseError.bind(null, options.store, options.router)
            ),
        ],
    };

    axios.defaults.withCredentials = true; // make sure we store/send cookies in CORS requests between frontend and backend

    app.config.globalProperties.$http = axios;
};

export default {
    interceptors, // Technically not needed, but will help with testing
    install,
    uninstall,
    encodeAxiosParam,
    getInitialTenantFromQuery,
};
